import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';

interface Attachment {
  id: number;
  signedURL: string;
}

interface AttachmentInfo {
  id: number;
  activationKey?: string;
}

// ── Component ────────────────────────────────────────────────────────

export function CompanyProfileScreen() {
  const { theme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Company info
  const [companyName, setCompanyName] = useState('');
  const [companyAddress1, setCompanyAddress1] = useState('');
  const [companyAddress2, setCompanyAddress2] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [companyZip, setCompanyZip] = useState('');
  const [companyCountry, setCompanyCountry] = useState('');
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState('');
  // Hidden required fields
  const [timeZoneId, setTimeZoneId] = useState<any>(null);
  const [currencyCodeDefault, setCurrencyCodeDefault] = useState<any>(null);
  const [weightUnitsDefault, setWeightUnitsDefault] = useState('');
  const [lengthUnitsDefault, setLengthUnitsDefault] = useState('');
  // Admin contact
  const [adminName, setAdminName] = useState('');
  const [adminPositionTitle, setAdminPositionTitle] = useState('');
  const [adminWorkPhone, setAdminWorkPhone] = useState('');
  const [adminCellPhone, setAdminCellPhone] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  // Technical contact
  const [technicalName, setTechnicalName] = useState('');
  const [technicalPositionTitle, setTechnicalPositionTitle] = useState('');
  const [technicalWorkPhone, setTechnicalWorkPhone] = useState('');
  const [technicalCellPhone, setTechnicalCellPhone] = useState('');
  const [technicalEmail, setTechnicalEmail] = useState('');
  // Finance contact
  const [financeName, setFinanceName] = useState('');
  const [financePositionTitle, setFinancePositionTitle] = useState('');
  const [financeWorkPhone, setFinanceWorkPhone] = useState('');
  const [financeCellPhone, setFinanceCellPhone] = useState('');
  const [financeEmail, setFinanceEmail] = useState('');
  // Logo
  const [companyLogo, setCompanyLogo] = useState<Attachment | null>(null);
  const [newLogoId, setNewLogoId] = useState<number | null>(null);
  const [newLogoActivationKey, setNewLogoActivationKey] = useState<string | null>(null);
  // State
  const [isEditing, setIsEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // address2/city/state/zip/country aren't editable from this screen, but we still load
  // them from the API and send them back unchanged on save. On HIGH-confidence Geocodio
  // results the backend overwrites them with canonical parsed components (matching the
  // call-card / NPSD pattern). On LOW/NONE results the backend leaves them untouched, so
  // the round-trip preserves whatever was previously stored.

  // Store original data for cancel
  const originalDataRef = useRef<Record<string, any> | null>(null);

  const storeOriginal = useCallback(() => {
    originalDataRef.current = {
      companyName, companyAddress1, companyAddress2, companyCity, companyState, companyZip,
      companyCountry, companyPhoneNumber,
      timeZoneId, currencyCodeDefault, weightUnitsDefault, lengthUnitsDefault,
      adminName, adminPositionTitle, adminWorkPhone, adminCellPhone, adminEmail,
      technicalName, technicalPositionTitle, technicalWorkPhone, technicalCellPhone, technicalEmail,
      financeName, financePositionTitle, financeWorkPhone, financeCellPhone, financeEmail,
      companyLogo,
    };
  }, [
    companyName, companyAddress1, companyAddress2, companyCity, companyState, companyZip,
    companyCountry, companyPhoneNumber,
    timeZoneId, currencyCodeDefault, weightUnitsDefault, lengthUnitsDefault,
    adminName, adminPositionTitle, adminWorkPhone, adminCellPhone, adminEmail,
    technicalName, technicalPositionTitle, technicalWorkPhone, technicalCellPhone, technicalEmail,
    financeName, financePositionTitle, financeWorkPhone, financeCellPhone, financeEmail,
    companyLogo,
  ]);

  const applyResult = useCallback((result: any) => {
    setCompanyName(result.companyName || '');
    setCompanyAddress1(result.companyAddress1 || '');
    setCompanyAddress2(result.companyAddress2 || '');
    setCompanyCity(result.companyCity || '');
    setCompanyState(result.companyState || '');
    setCompanyZip(result.companyZip || '');
    setCompanyCountry(result.companyCountry || '');
    setCompanyPhoneNumber(result.companyPhoneNumber || '');
    setTimeZoneId(result.timeZoneId);
    setCurrencyCodeDefault(result.currencyCodeDefault);
    setWeightUnitsDefault(result.weightUnitsDefault || '');
    setLengthUnitsDefault(result.lengthUnitsDefault || '');
    setAdminName(result.adminName || '');
    setAdminPositionTitle(result.adminPositionTitle || '');
    setAdminWorkPhone(result.adminWorkPhone || '');
    setAdminCellPhone(result.adminCellPhone || '');
    setAdminEmail(result.adminEmail || '');
    setTechnicalName(result.technicalName || '');
    setTechnicalPositionTitle(result.technicalPositionTitle || '');
    setTechnicalWorkPhone(result.technicalWorkPhone || '');
    setTechnicalCellPhone(result.technicalCellPhone || '');
    setTechnicalEmail(result.technicalEmail || '');
    setFinanceName(result.financeName || '');
    setFinancePositionTitle(result.financePositionTitle || '');
    setFinanceWorkPhone(result.financeWorkPhone || '');
    setFinanceCellPhone(result.financeCellPhone || '');
    setFinanceEmail(result.financeEmail || '');
    setCompanyLogo(result.companyLogo || null);
    setNewLogoId(null);
    setNewLogoActivationKey(null);
  }, []);

  // Initial load
  useEffect(() => {
    api.getCompanyProfile((result: any) => {
      applyResult(result);
      setLoaded(true);
    });
  }, [applyResult]);

  // Store original whenever editing starts
  const enableEditing = useCallback(() => {
    storeOriginal();
    setIsEditing(true);
  }, [storeOriginal]);

  const cancelEditing = useCallback(() => {
    if (originalDataRef.current) {
      const d = originalDataRef.current;
      setCompanyName(d.companyName);
      setCompanyAddress1(d.companyAddress1);
      setCompanyAddress2(d.companyAddress2);
      setCompanyCity(d.companyCity);
      setCompanyState(d.companyState);
      setCompanyZip(d.companyZip);
      setCompanyCountry(d.companyCountry);
      setCompanyPhoneNumber(d.companyPhoneNumber);
      setTimeZoneId(d.timeZoneId);
      setCurrencyCodeDefault(d.currencyCodeDefault);
      setWeightUnitsDefault(d.weightUnitsDefault);
      setLengthUnitsDefault(d.lengthUnitsDefault);
      setAdminName(d.adminName);
      setAdminPositionTitle(d.adminPositionTitle);
      setAdminWorkPhone(d.adminWorkPhone);
      setAdminCellPhone(d.adminCellPhone);
      setAdminEmail(d.adminEmail);
      setTechnicalName(d.technicalName);
      setTechnicalPositionTitle(d.technicalPositionTitle);
      setTechnicalWorkPhone(d.technicalWorkPhone);
      setTechnicalCellPhone(d.technicalCellPhone);
      setTechnicalEmail(d.technicalEmail);
      setFinanceName(d.financeName);
      setFinancePositionTitle(d.financePositionTitle);
      setFinanceWorkPhone(d.financeWorkPhone);
      setFinanceCellPhone(d.financeCellPhone);
      setFinanceEmail(d.financeEmail);
      setCompanyLogo(d.companyLogo);
    }
    setNewLogoId(null);
    setNewLogoActivationKey(null);
    setIsEditing(false);
  }, []);

  const saveProfile = useCallback(() => {
    let companyLogoInfo: any = null;
    if (newLogoId && newLogoActivationKey) {
      companyLogoInfo = { id: newLogoId, activationKey: newLogoActivationKey };
    } else if (companyLogo?.id) {
      companyLogoInfo = { id: companyLogo.id };
    }

    const profileData = {
      companyName, companyAddress1, companyAddress2, companyCity, companyState, companyZip,
      companyCountry, companyPhoneNumber,
      timeZoneId, currencyCodeDefault, weightUnitsDefault, lengthUnitsDefault,
      adminName, adminPositionTitle, adminWorkPhone, adminCellPhone, adminEmail,
      technicalName, technicalPositionTitle, technicalWorkPhone, technicalCellPhone, technicalEmail,
      financeName, financePositionTitle, financeWorkPhone, financeCellPhone, financeEmail,
      companyLogoInfo,
    };

    api.updateCompanyProfile(profileData, () => {
      App.showPopupNotification('Save Successful', 'success');
      setIsEditing(false);
      setNewLogoId(null);
      setNewLogoActivationKey(null);
      // Refresh from server for updated signed URLs
      api.getCompanyProfile((result: any) => {
        applyResult(result);
      });
      // Update home screen logo
      App.preloadCompanyLogo();
    });
  }, [
    companyName, companyAddress1, companyAddress2, companyCity, companyState, companyZip,
    companyCountry, companyPhoneNumber,
    timeZoneId, currencyCodeDefault, weightUnitsDefault, lengthUnitsDefault,
    adminName, adminPositionTitle, adminWorkPhone, adminCellPhone, adminEmail,
    technicalName, technicalPositionTitle, technicalWorkPhone, technicalCellPhone, technicalEmail,
    financeName, financePositionTitle, financeWorkPhone, financeCellPhone, financeEmail,
    companyLogo, newLogoId, newLogoActivationKey, applyResult,
  ]);

  const triggerLogoUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onLogoFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) return;

    App.uploadFromFileInput(fileInput, {
      successCallback: (_idx: number, attachment: Attachment) => {
        setCompanyLogo({ id: 0, signedURL: attachment.signedURL });
        App.showPopupNotification('Logo uploaded. Click Update to save.', 'success');
      },
      updateAttachmentInfo: (_idx: number, attachmentInfo: AttachmentInfo) => {
        setNewLogoId(attachmentInfo.id);
        setNewLogoActivationKey(attachmentInfo.activationKey || null);
      },
      updateAttachment: () => {},
    });
  }, []);

  const profileLoaded = companyName !== null && loaded;

  return (
    <div className={theme}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header kst-profile-header">
                <div className="kst-profile-header-content">
                  <h3 className="kst-page-title">{companyName || 'Company Profile'}</h3>
                  <p className="kst-page-description">
                    Manage your company information and contacts.
                  </p>
                </div>
                <div className="kst-profile-header-actions">
                  {!isEditing ? (
                    <button type="button" className="kstc-btn kstc-btn--primary" onClick={enableEditing}>Edit Information</button>
                  ) : (
                    <>
                      <button type="button" className="kstc-btn kstc-btn--primary" onClick={saveProfile}>Update</button>
                      <button type="button" className="kstc-btn kstc-btn--secondary" onClick={cancelEditing}>Cancel</button>
                    </>
                  )}
                </div>
              </div>

              {profileLoaded ? (
                <div className="kst-profile-form">
                  <div className="kst-profile-grid">
                    {/* Company Information — Name & Logo grouped first; they appear on customer-facing output */}
                    <div className="kst-profile-section">
                      <h4 className="kst-profile-section-title">Company Information</h4>
                      <div className="kst-profile-section-body">
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="companyName">Name</label>
                          <input type="text" id="companyName" className="kstc-input" value={companyName} disabled={!isEditing} onChange={(e) => setCompanyName(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label">Logo</label>
                          <div className="kst-logo-container">
                            {companyLogo?.signedURL ? (
                              <img src={companyLogo.signedURL} alt="Company Logo" className="kst-logo-image" />
                            ) : (
                              <span className="kst-no-logo">No logo uploaded</span>
                            )}
                            {isEditing && (
                              <button type="button" className="kstc-btn kstc-btn--secondary kst-logo-btn" onClick={triggerLogoUpload}>
                                {companyLogo?.signedURL ? 'Change Logo' : 'Upload Logo'}
                              </button>
                            )}
                            <input ref={fileInputRef} type="file" id="companyLogoFileInput" accept=".png,.jpg,.jpeg" style={{ display: 'none' }} onChange={onLogoFileChange} />
                          </div>
                          <div style={{ fontStyle: 'italic', fontSize: '0.85em', opacity: 0.7, marginTop: '4px' }}>
                            Name and logo appear on case reports and other customer-facing documents.
                          </div>
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="companyAddress1">Street</label>
                          <input type="text" id="companyAddress1" className="kstc-input" value={companyAddress1} disabled={!isEditing} onChange={(e) => setCompanyAddress1(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="companyAddress2">Address Line 2</label>
                          <input type="text" id="companyAddress2" className="kstc-input" value={companyAddress2} disabled={!isEditing} onChange={(e) => setCompanyAddress2(e.target.value)} />
                        </div>
                        <div className="kstc-form-row">
                          <div className="kstc-form-group kstc-form-group--half">
                            <label className="kstc-label" htmlFor="companyCity">City</label>
                            <input type="text" id="companyCity" className="kstc-input" value={companyCity} disabled={!isEditing} onChange={(e) => setCompanyCity(e.target.value)} />
                          </div>
                          <div className="kstc-form-group kstc-form-group--half">
                            <label className="kstc-label" htmlFor="companyState">State / Province</label>
                            <input type="text" id="companyState" className="kstc-input" value={companyState} disabled={!isEditing} onChange={(e) => setCompanyState(e.target.value)} />
                          </div>
                        </div>
                        <div className="kstc-form-row">
                          <div className="kstc-form-group kstc-form-group--half">
                            <label className="kstc-label" htmlFor="companyZip">ZIP / Postal Code</label>
                            <input type="text" id="companyZip" className="kstc-input" value={companyZip} disabled={!isEditing} onChange={(e) => setCompanyZip(e.target.value)} />
                          </div>
                          <div className="kstc-form-group kstc-form-group--half">
                            <label className="kstc-label" htmlFor="companyCountry">Country</label>
                            <input type="text" id="companyCountry" className="kstc-input" value={companyCountry} disabled={!isEditing} onChange={(e) => setCompanyCountry(e.target.value)} />
                          </div>
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="companyPhone">Phone</label>
                          <input type="tel" id="companyPhone" className="kstc-input" value={companyPhoneNumber} disabled={!isEditing} onChange={(e) => setCompanyPhoneNumber(e.target.value)} />
                        </div>
                      </div>
                    </div>

                    {/* Administrator Contact */}
                    <div className="kst-profile-section">
                      <h4 className="kst-profile-section-title">Administrator Contact</h4>
                      <div className="kst-profile-section-body">
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="adminName">Name</label>
                          <input type="text" id="adminName" className="kstc-input" value={adminName} disabled={!isEditing} onChange={(e) => setAdminName(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="adminTitle">Position Title</label>
                          <input type="text" id="adminTitle" className="kstc-input" value={adminPositionTitle} disabled={!isEditing} onChange={(e) => setAdminPositionTitle(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="adminWorkPhone">Work Phone</label>
                          <input type="tel" id="adminWorkPhone" className="kstc-input" value={adminWorkPhone} disabled={!isEditing} onChange={(e) => setAdminWorkPhone(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="adminCellPhone">Cell Phone</label>
                          <input type="tel" id="adminCellPhone" className="kstc-input" value={adminCellPhone} disabled={!isEditing} onChange={(e) => setAdminCellPhone(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="adminEmail">E-mail</label>
                          <input type="email" id="adminEmail" className="kstc-input" value={adminEmail} disabled={!isEditing} onChange={(e) => setAdminEmail(e.target.value)} />
                        </div>
                      </div>
                    </div>

                    {/* Technical Contact */}
                    <div className="kst-profile-section">
                      <h4 className="kst-profile-section-title">Technical Contact</h4>
                      <div className="kst-profile-section-body">
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="technicalName">Name</label>
                          <input type="text" id="technicalName" className="kstc-input" value={technicalName} disabled={!isEditing} onChange={(e) => setTechnicalName(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="technicalTitle">Position Title</label>
                          <input type="text" id="technicalTitle" className="kstc-input" value={technicalPositionTitle} disabled={!isEditing} onChange={(e) => setTechnicalPositionTitle(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="technicalWorkPhone">Work Phone</label>
                          <input type="tel" id="technicalWorkPhone" className="kstc-input" value={technicalWorkPhone} disabled={!isEditing} onChange={(e) => setTechnicalWorkPhone(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="technicalCellPhone">Cell Phone</label>
                          <input type="tel" id="technicalCellPhone" className="kstc-input" value={technicalCellPhone} disabled={!isEditing} onChange={(e) => setTechnicalCellPhone(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="technicalEmail">E-mail</label>
                          <input type="email" id="technicalEmail" className="kstc-input" value={technicalEmail} disabled={!isEditing} onChange={(e) => setTechnicalEmail(e.target.value)} />
                        </div>
                      </div>
                    </div>

                    {/* Finance Contact */}
                    <div className="kst-profile-section">
                      <h4 className="kst-profile-section-title">Finance Contact</h4>
                      <div className="kst-profile-section-body">
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="financeName">Name</label>
                          <input type="text" id="financeName" className="kstc-input" value={financeName} disabled={!isEditing} onChange={(e) => setFinanceName(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="financeTitle">Position Title</label>
                          <input type="text" id="financeTitle" className="kstc-input" value={financePositionTitle} disabled={!isEditing} onChange={(e) => setFinancePositionTitle(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="financeWorkPhone">Work Phone</label>
                          <input type="tel" id="financeWorkPhone" className="kstc-input" value={financeWorkPhone} disabled={!isEditing} onChange={(e) => setFinanceWorkPhone(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="financeCellPhone">Cell Phone</label>
                          <input type="tel" id="financeCellPhone" className="kstc-input" value={financeCellPhone} disabled={!isEditing} onChange={(e) => setFinanceCellPhone(e.target.value)} />
                        </div>
                        <div className="kst-form-group">
                          <label className="kstc-label" htmlFor="financeEmail">E-mail</label>
                          <input type="email" id="financeEmail" className="kstc-input" value={financeEmail} disabled={!isEditing} onChange={(e) => setFinanceEmail(e.target.value)} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="kst-loading"></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyProfileScreen;

